import { ErrorNotice } from "../ui/ErrorNotice.js";
/**
 * The add-choice form for one option group: value, label, admin-only prompt and a
 * reference image. Extracted from OptionsPanel so GroupCard can host it directly under
 * the group's existing choices.
 *
 * P2 (round-trip audit): the "required" checkbox was removed — nothing has ever enforced
 * the flag (see ChoiceRow). `order` is now sent explicitly: the server already sorts
 * choices by it, but every create left it at the default 0, so the whole list tied on
 * zero and the display order was whatever the DB happened to return. New choices append.
 */
import { useRef, useState } from "react";
import { useServices } from "../app/context.js";
import type { OptionChoice, OptionGroup } from "@genz/platform";
import { ChoiceReferenceImage } from "./ChoiceReferenceImage.js";

/** Append position for a new choice: one past the highest existing `order`. */
function nextOrder(group: OptionGroup): number {
  return group.choices.length ? Math.max(...group.choices.map((c) => c.order)) + 1 : 0;
}

export function AddChoiceForm({ group, onCreated }: { group: OptionGroup; onCreated: (c: OptionChoice) => void }) {
  const { client } = useServices();
  const [value, setValue] = useState("");
  const [label, setLabel] = useState("");
  const [harness, setHarness] = useState("");
  const [referenceRef, setReferenceRef] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const fieldRef = useRef<HTMLInputElement>(null);
  const [fieldError, setFieldError] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    if (!value.trim()) return;
    setBusy(true);
    setError(null);
    setFieldError(false);
    try {
      const created = await client.adminCreateOptionChoice({
        group_id: group.id,
        value: value.trim(),
        label: label.trim() || value.trim(),
        harness_prompt: harness.trim() || null,
        reference_ref: referenceRef,
        order: nextOrder(group),
      });
      onCreated(created);
      setValue("");
      setLabel("");
      setHarness("");
      setReferenceRef(null);
    } catch (e) {
      const detail = e instanceof Error ? e.message : String(e);
      setError(detail);
      if (/duplicate|already|key exists|value exists|409/i.test(detail)) {
        setFieldError(true);
        requestAnimationFrame(() => fieldRef.current?.focus());
      }
    } finally {
      setBusy(false);
    }
  }

  const tid = `option-group-${group.id}-add-choice`;
  return (
    <div className="options-form-row" data-testid={tid}>
      <div className="field">
        <label htmlFor={`${tid}-value`}>Value</label>
        <input
          id={`${tid}-value`}
          className="input"
          type="text"
          value={value}
          data-testid={`${tid}-value`}
          onChange={(e) => { setValue(e.target.value); if (fieldError) { setFieldError(false); setError(null); } }}
          ref={fieldRef}
          aria-invalid={fieldError || undefined}
          aria-describedby={fieldError ? `${tid}-field-error` : undefined}
        />
        {fieldError && <span id={`${tid}-field-error`} className="field-error" role="alert"><ErrorNotice detail="duplicate option value" /><span>Choose a different value, then try again.</span></span>}
      </div>
      <label className="field">
        <span>Label</span>
        <input
          className="input"
          type="text"
          value={label}
          data-testid={`${tid}-label`}
          onChange={(e) => setLabel(e.target.value)}
        />
      </label>
      <label className="field options-choice__harness">
        <span>Prompt (admin-only)</span>
        <textarea
          className="input"
          rows={2}
          value={harness}
          data-testid={`${tid}-harness`}
          onChange={(e) => setHarness(e.target.value)}
        />
      </label>
      <label className="field">
        <span>Reference</span>
        <ChoiceReferenceImage
          value={referenceRef}
          onChange={setReferenceRef}
          testid={`${tid}-ref`}
          groupId={group.id}
          choiceValue={value.trim() || "choice"}
        />
      </label>
      {/* WS-Z5 rule 7: a sub-form action inside an expanded group, not the panel's one
          invitation — that is "+ New option group" in the `.actionbar` at the bottom. */}
      <button
        type="button"
        className="btn btn--sm"
        disabled={busy || !value.trim()}
        data-testid={`${tid}-submit`}
        onClick={() => void submit()}
      >
        {busy ? "Adding…" : "+ choice"}
      </button>
      {error && (
        <span role="alert" data-testid={`${tid}-error`} className="faint">
          <ErrorNotice detail={error} />
        </span>
      )}
    </div>
  );
}
